/**
 * Video Recording Mode Settings
 *
 * Extracted from customer_portal VideoRecordingModeSetting.tsx and LegacyVideoRecordingModeSetting.tsx.
 * Contains recording mode maps, enums, and bitrate constants for video capture settings.
 *
 * @packageDocumentation
 */

import { RecordingMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/recording_mode";

import { isX10 } from "./constraints";

import type { VehicleConfiguration } from "./constraints";

// ============================================================================
// X10 Video Recording Mode (Resolution × AspectRatio × Mode)
// ============================================================================

export enum VideoResolution {
  RES_1080P = "Full HD (1080p)",
  RES_4K = "4K (2160p)",
}

export enum VideoAspectRatio {
  ASPECT_16_9 = "16:9",
  ASPECT_4_3 = "4:3",
}

export enum VideoMode {
  STANDARD = "Standard",
  HDR = "HDR",
  LOW_LIGHT = "Low Light",
}

export type X10ParsedVideoMode = {
  resolution: VideoResolution;
  aspectRatio: VideoAspectRatio;
  mode: VideoMode;
};

export const X10_VIDEO_RECORDING_MODE_MAP = new Map<RecordingMode_Enum, X10ParsedVideoMode>([
  // 4K Standard
  [
    RecordingMode_Enum.VIDEO_4K_DEEP_30FPS,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_DEEP_FULL_30FPS,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.STANDARD,
    },
  ],
  // 4K Low Light
  [
    RecordingMode_Enum.VIDEO_4K_24FPS,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.LOW_LIGHT,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_FULL_24FPS,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.LOW_LIGHT,
    },
  ],
  // 4K HDR
  [
    RecordingMode_Enum.VIDEO_4K_30FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_FULL_30FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.HDR,
    },
  ],
  // 1080p Standard
  [
    RecordingMode_Enum.VIDEO_1080P_DEEP_30FPS,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_1080P_DEEP_FULL_30FPS,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.STANDARD,
    },
  ],
  // 1080p Low Light
  [
    RecordingMode_Enum.VIDEO_1080P_24FPS,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.LOW_LIGHT,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_1080P_FULL_24FPS,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.LOW_LIGHT,
    },
  ],
  // 1080p HDR
  [
    RecordingMode_Enum.VIDEO_1080P_30FPS_HDR,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_16_9,
      mode: VideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_1080P_FULL_30FPS_HDR,
    {
      resolution: VideoResolution.RES_1080P,
      aspectRatio: VideoAspectRatio.ASPECT_4_3,
      mode: VideoMode.HDR,
    },
  ],
]);

const x10VideoInverseMap = new Map<string, RecordingMode_Enum>();
for (const [enumValue, parsed] of X10_VIDEO_RECORDING_MODE_MAP.entries()) {
  const key = `${parsed.resolution}_${parsed.aspectRatio}_${parsed.mode}`;
  x10VideoInverseMap.set(key, enumValue);
}

export function getX10VideoRecordingMode(
  resolution: VideoResolution,
  aspectRatio: VideoAspectRatio,
  mode: VideoMode
): RecordingMode_Enum | undefined {
  return x10VideoInverseMap.get(`${resolution}_${aspectRatio}_${mode}`);
}

export function parseX10VideoRecordingMode(
  value: RecordingMode_Enum
): X10ParsedVideoMode | undefined {
  return X10_VIDEO_RECORDING_MODE_MAP.get(value);
}

export const X10_VIDEO_RESOLUTION_OPTIONS = [
  { key: VideoResolution.RES_4K, value: VideoResolution.RES_4K, label: VideoResolution.RES_4K },
  {
    key: VideoResolution.RES_1080P,
    value: VideoResolution.RES_1080P,
    label: VideoResolution.RES_1080P,
  },
];

export const X10_VIDEO_ASPECT_RATIO_OPTIONS = [
  {
    key: VideoAspectRatio.ASPECT_16_9,
    value: VideoAspectRatio.ASPECT_16_9,
    label: VideoAspectRatio.ASPECT_16_9,
  },
  {
    key: VideoAspectRatio.ASPECT_4_3,
    value: VideoAspectRatio.ASPECT_4_3,
    label: VideoAspectRatio.ASPECT_4_3,
  },
];

export const X10_VIDEO_MODE_OPTIONS = [
  { key: VideoMode.STANDARD, value: VideoMode.STANDARD, label: VideoMode.STANDARD },
  { key: VideoMode.HDR, value: VideoMode.HDR, label: VideoMode.HDR },
  { key: VideoMode.LOW_LIGHT, value: VideoMode.LOW_LIGHT, label: VideoMode.LOW_LIGHT },
];

export const DEFAULT_X10_VIDEO_MODE: X10ParsedVideoMode = {
  resolution: VideoResolution.RES_4K,
  aspectRatio: VideoAspectRatio.ASPECT_16_9,
  mode: VideoMode.STANDARD,
};

// ============================================================================
// Legacy Video Recording Mode (S2/X2: Resolution × FPS × Mode)
// ============================================================================

export enum LegacyVideoFps {
  FPS_24 = "24",
  FPS_30 = "30",
  FPS_48 = "48",
  FPS_60 = "60",
}

export enum LegacyVideoMode {
  STANDARD = "Standard",
  HDR = "HDR",
}

export type LegacyParsedVideoMode = {
  resolution: VideoResolution;
  fps: LegacyVideoFps;
  mode: LegacyVideoMode;
};

export const LEGACY_VIDEO_RECORDING_MODE_MAP = new Map<RecordingMode_Enum, LegacyParsedVideoMode>([
  // 4K Standard
  [
    RecordingMode_Enum.VIDEO_4K_24FPS,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_24,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_30FPS,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_30,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_48FPS,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_48,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_60FPS,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_60,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  // 4K HDR
  [
    RecordingMode_Enum.VIDEO_4K_24FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_24,
      mode: LegacyVideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_30FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_30,
      mode: LegacyVideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_48FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_48,
      mode: LegacyVideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_4K_60FPS_HDR,
    {
      resolution: VideoResolution.RES_4K,
      fps: LegacyVideoFps.FPS_60,
      mode: LegacyVideoMode.HDR,
    },
  ],
  // 1080p Standard
  [
    RecordingMode_Enum.VIDEO_1080P_30FPS,
    {
      resolution: VideoResolution.RES_1080P,
      fps: LegacyVideoFps.FPS_30,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_1080P_60FPS,
    {
      resolution: VideoResolution.RES_1080P,
      fps: LegacyVideoFps.FPS_60,
      mode: LegacyVideoMode.STANDARD,
    },
  ],
  // 1080p HDR
  [
    RecordingMode_Enum.VIDEO_1080P_30FPS_HDR,
    {
      resolution: VideoResolution.RES_1080P,
      fps: LegacyVideoFps.FPS_30,
      mode: LegacyVideoMode.HDR,
    },
  ],
  [
    RecordingMode_Enum.VIDEO_1080P_60FPS_HDR,
    {
      resolution: VideoResolution.RES_1080P,
      fps: LegacyVideoFps.FPS_60,
      mode: LegacyVideoMode.HDR,
    },
  ],
]);

const legacyVideoInverseMap = new Map<string, RecordingMode_Enum>();
for (const [enumValue, parsed] of LEGACY_VIDEO_RECORDING_MODE_MAP.entries()) {
  const key = `${parsed.resolution}_${parsed.fps}_${parsed.mode}`;
  legacyVideoInverseMap.set(key, enumValue);
}

export function getLegacyVideoRecordingMode(
  resolution: VideoResolution,
  fps: LegacyVideoFps,
  mode: LegacyVideoMode
): RecordingMode_Enum | undefined {
  return legacyVideoInverseMap.get(`${resolution}_${fps}_${mode}`);
}

export function parseLegacyVideoRecordingMode(
  value: RecordingMode_Enum
): LegacyParsedVideoMode | undefined {
  return LEGACY_VIDEO_RECORDING_MODE_MAP.get(value);
}

/** For 1080p, only 30 and 60 fps are valid */
export const LEGACY_1080P_VALID_FPS = [LegacyVideoFps.FPS_30, LegacyVideoFps.FPS_60];

export const LEGACY_VIDEO_RESOLUTION_OPTIONS = [
  { key: VideoResolution.RES_4K, value: VideoResolution.RES_4K, label: VideoResolution.RES_4K },
  {
    key: VideoResolution.RES_1080P,
    value: VideoResolution.RES_1080P,
    label: VideoResolution.RES_1080P,
  },
];

export const LEGACY_VIDEO_FPS_OPTIONS = [
  { key: LegacyVideoFps.FPS_24, value: LegacyVideoFps.FPS_24, label: LegacyVideoFps.FPS_24 },
  { key: LegacyVideoFps.FPS_30, value: LegacyVideoFps.FPS_30, label: LegacyVideoFps.FPS_30 },
  { key: LegacyVideoFps.FPS_48, value: LegacyVideoFps.FPS_48, label: LegacyVideoFps.FPS_48 },
  { key: LegacyVideoFps.FPS_60, value: LegacyVideoFps.FPS_60, label: LegacyVideoFps.FPS_60 },
];

export const LEGACY_VIDEO_MODE_OPTIONS = [
  {
    key: LegacyVideoMode.STANDARD,
    value: LegacyVideoMode.STANDARD,
    label: LegacyVideoMode.STANDARD,
  },
  { key: LegacyVideoMode.HDR, value: LegacyVideoMode.HDR, label: LegacyVideoMode.HDR },
];

export const DEFAULT_LEGACY_VIDEO_MODE: LegacyParsedVideoMode = {
  resolution: VideoResolution.RES_4K,
  fps: LegacyVideoFps.FPS_30,
  mode: LegacyVideoMode.STANDARD,
};

export function getAvailableLegacyFpsOptions(resolution: VideoResolution) {
  if (resolution === VideoResolution.RES_1080P) {
    return LEGACY_VIDEO_FPS_OPTIONS.filter(
      option => option.value === LegacyVideoFps.FPS_30 || option.value === LegacyVideoFps.FPS_60
    );
  }
  return LEGACY_VIDEO_FPS_OPTIONS;
}

// ============================================================================
// Vehicle-aware video recording mode helpers
// ============================================================================

export function parseVideoRecordingMode(
  value: RecordingMode_Enum,
  vehicleConfig?: VehicleConfiguration
): X10ParsedVideoMode | LegacyParsedVideoMode | undefined {
  if (isX10(vehicleConfig)) {
    return parseX10VideoRecordingMode(value);
  }
  return parseLegacyVideoRecordingMode(value);
}

// ============================================================================
// Custom Bitrate
// ============================================================================

export const BITS_IN_MEGABITS = 1e6;

/** Bitrate range in Mb/s (10-50). Not available on X10. */
export const CUSTOM_BITRATE_RANGE = [...Array(51).keys()].splice(10);

export const CUSTOM_BITRATE_MIN = 10;
export const CUSTOM_BITRATE_MAX = 50;
export const CUSTOM_BITRATE_DEFAULT = 50;

export function formatBitrate(value: number): string {
  return `${value} Mb/s`;
}

export function isCustomBitrateAvailable(vehicleConfig?: VehicleConfiguration): boolean {
  return !isX10(vehicleConfig);
}
